import { query, queryK } from 'sdi/shape';
import {
    Feature,
    FeatureCollection,
    RemoteResource,
    remoteToOption,
    remoteNone,
    GeometryType,
} from 'sdi/source';
import {
    Evaluation,
    EvaluationIO,
    EvaluationList,
    GlobalNote,
    Issue,
    IssueCriticality,
    IssueCriticalityIO,
    IssueNote,
    IssueOrigin,
    IssueOriginIO,
    IssueStatus,
    IssueStatusIO,
    IssueType,
    OsmObject,
    OsmType,
    OsmUser,
    IssueTypeOsmObject,
    IssueTypeOsmObjectIO,
    CurrentOsmUser,
    ExternalIssue,
    Theme,
} from '../remote';
import { Option, fromNullable, none, some } from 'fp-ts/lib/Option';
import { getFilteredAndSortedRows, getSelectedIssueIds } from './table';
import debug = require('debug');
import { scopeOption } from 'sdi/lib';
import { Duration } from '../events/route';
import tr from 'sdi/locale';
import { getRenderGlobalDuration } from './app';
import { index } from 'fp-ts/lib/Array';
import { right } from 'fp-ts/lib/Either';
import { FetchData } from 'sdi/map';
import { GlobalFilter } from '../events/qa';
import { MapillaryImageGroup } from '../remote/mapillary';
import { wgs84toLB72 } from '../queries/geom';

const logger = debug('sdi:queries/qa');

export const getCriticalityColor = (code: IssueCriticality | null) => {
    if (code !== null) {
        switch (code) {
            case 'high':
                return '#CE3943';
            case 'medium':
                return '#EF7924';
            case 'low':
                return '#FACC03';
            // default: return '#9F9F9F';
        }
    }
    return 'black';
};

export const getOriginColor = (code: IssueOrigin) => {
    switch (code) {
        case 'CHANGE_DETECTION_CREATION':
            return '#42DD73';
        case 'CHANGE_DETECTION_MODIFICATION':
            return '#ffb03b';
        case 'CHANGE_DETECTION_DELETE':
            return '#d93838';
        // case 'COMPARISON':
        //     return '#2BA6FF';
        case 'QA':
            return '#900099';
        // case 'ROUTING':
        //     return '#191760';
        case 'CREATION_OR_MODIFICATION':
        default:
            return '#9F9F9F';
    }
};

export const getOriginList = (): IssueOrigin[] =>
    IssueOriginIO.types.map(o => o.value); // nice trick :) - pm

export const getTypeObjectList = (): IssueTypeOsmObject[] =>
    IssueTypeOsmObjectIO.types.map(o => o.value).sort();

export const getThemesRemote = queryK('qa/data/themes');
export const getIssueListRemote = queryK('qa/data/issues');
export const getIssueTypesRemote = queryK('qa/data/issue-types');
export const getEvaluationListRemote = queryK('qa/data/evaluations');
export const getIssueNoteListRemote = queryK('qa/data/notes/issue');
export const getGlobalNoteListRemote = queryK('qa/data/notes/global');
export const getCurrentOsmUserRemote = queryK('qa/data/osm/user');
export const getExternalIssueRemote = queryK('qa/data/external-issue');
export const getMapillaryImageRemote = (
    issueId: number
): Option<RemoteResource<MapillaryImageGroup>> =>
    fromNullable(query('qa/data/mapillary/images')[issueId.toString()]);

export const getIssueUserIdRemote = queryK('qa/data/issue/user');

export type OsmResourceId = { id: number; type: OsmType; version: number };
export const resourceId = (osmId: OsmResourceId) => osmId.id;
export const resourceVersion = (osmId: OsmResourceId) => osmId.version;
export const osmResourceIdkey = ({ id, type, version }: OsmResourceId) =>
    `${id}/${type}/${version}`;

export const getOsmObjectRemote = (
    osmId: OsmResourceId
): Option<RemoteResource<OsmObject>> =>
    fromNullable(query('qa/issues/select/osm-object')[osmResourceIdkey(osmId)]);

export const getPreviousOsmObjectRemote = (
    osmId: OsmResourceId
): Option<RemoteResource<OsmObject>> =>
    getPreviousOsmResourceId(osmId).chain(id =>
        fromNullable(query('qa/issues/select/osm-object')[osmResourceIdkey(id)])
    );

export const getPreviousOsmResourceId = (osmId: OsmResourceId) => {
    if (osmId.version > 1) {
        const oldVersion = osmId.version - 1;
        return some({ id: osmId.id, type: osmId.type, version: oldVersion });
    } else {
        return none;
    }
};

export const getThemes = (): Option<Theme[]> =>
    remoteToOption(getThemesRemote());
export const getIssueList = (): Option<Issue[]> =>
    remoteToOption(getIssueListRemote());
export const getIssueTypes = (): Option<IssueType[]> =>
    remoteToOption(getIssueTypesRemote());
export const getEvaluationList = (): Option<EvaluationList[]> =>
    remoteToOption(getEvaluationListRemote());
export const getIssueNoteList = (): Option<IssueNote[]> =>
    remoteToOption(getIssueNoteListRemote());
export const getGlobalNoteList = (): Option<GlobalNote[]> =>
    remoteToOption(getGlobalNoteListRemote());
export const getCurrentOsmUser = (): Option<CurrentOsmUser> =>
    remoteToOption(getCurrentOsmUserRemote());
export const getMapillaryImage = (
    issueId: number
): Option<MapillaryImageGroup> =>
    getMapillaryImageRemote(issueId).chain(img => remoteToOption(img));

export const getIssueOsmUser = (): Option<OsmUser> =>
    remoteToOption(getIssueUserIdRemote());
export const getExternalIssue = (): Option<ExternalIssue> =>
    remoteToOption(getExternalIssueRemote());

export const getIssueOsmUserId = () => getIssueOsmUser().map(u => u.id);

export const getCurrentOsmUserName = () =>
    getCurrentOsmUser().chain(u =>
        fromNullable(u.openstreetmap).map(osm => osm.extras.display_name)
    );
export const getCurrentOsmUid = () =>
    getCurrentOsmUser().chain(u =>
        fromNullable(u.openstreetmap).map(osm => osm.uid)
    );

export const issueToFeature = (issue: Issue, filtered: boolean): Feature => ({
    type: 'Feature',
    geometry: wgs84toLB72(issue.point),
    id: issue.id,
    properties: {
        timestamp: issue.timestamp,
        priority: !filtered ? 'false' : issue.criticity,
        origin: !filtered
            ? 'false'
            : issue.origin
            ? issue.origin
            : getOrigin(issue.issue_type).getOrElse('CREATION_OR_MODIFICATION'),
        object: !filtered
            ? 'false'
            : getTypeObject(issue.issue_type)
                  .map<string>(ot => ot)
                  .getOrElse('nn'),
        filtered,
    },
});

export const issueListToLayer = (ls: Issue[]): FeatureCollection => {
    const filtered = getFilteredAndSortedRows(ls).map(r => r.from);
    return {
        type: 'FeatureCollection',
        features: ls.map(i => issueToFeature(i, filtered.indexOf(i.id) >= 0)),
    };
};

export const findIssue = (id: number | string) =>
    getIssueList().chain(issues => fromNullable(issues.find(i => i.id === id)));

// Only for first tests :)
// export const getTestFeature = () => getIssueList().map(il => issueToFeature(il[0]));
// export const getTestIssue = () => getIssueList().map(il => il[0]);
export const getLastSelectedIssue = (): Option<Issue> => {
    const len = getSelectedIssueIds().length;
    if (len > 0) {
        const selectId = getSelectedIssueIds()[len - 1];
        return getIssueList().chain(il =>
            fromNullable(il.find(i => i.id === selectId))
        );
    } else {
        return none;
    }
};

export const getType = (typeId: number) =>
    getIssueTypes().chain(types =>
        fromNullable(types.find(t => t.id === typeId))
    );

export const getDefaultCriticality = (typeId: number | null) =>
    fromNullable(typeId).chain(tid =>
        getType(tid).chain(t => fromNullable(t.criticity))
    );

export const getCriticalityOpt = (issue: Issue) =>
    fromNullable(issue.criticity).foldL(
        () => getDefaultCriticality(issue.issue_type),
        c => some(c)
    );
export const getCriticalityOrUnknown = (
    issue: Issue
): IssueCriticality | 'unknown' =>
    getCriticalityOpt(issue).foldL(
        () => 'unknown',
        c => c
    );

const getLongDescription = (typeId: number | null) =>
    fromNullable(typeId)
        .chain(tid => getType(tid).map(t => t.description))
        .getOrElse('');

export const getDescription = (typeId: number | null) =>
    getLongDescription(typeId) === ''
        ? fromNullable(typeId)
              .chain(tid => getType(tid).map(t => t.short_description))
              .getOrElse(tr.qa('unknown'))
        : getLongDescription(typeId);

export const getOrigin = (typeId: number | null) =>
    fromNullable(typeId).chain(tid =>
        getType(tid).chain(t => fromNullable(t.origin))
    );

export const getTypeObject = (typeId: number | null) =>
    fromNullable(typeId).chain(tid =>
        getType(tid).chain(t => fromNullable(t.osm_object))
    );

export const getStatusList = (): IssueStatus[] =>
    IssueStatusIO.types.map(t => t.value);
export const getStatus = (issue: Issue) =>
    fromNullable(issue.status).getOrElse('RAISED');

export const getCriticalityList = (): IssueCriticality[] =>
    IssueCriticalityIO.types.map(t => t.value);

export const getEvaluationOpt = (userName: string | null) =>
    getEvaluationList().chain(list =>
        fromNullable(list.find(e => e.user_name === userName)).chain(e =>
            fromNullable(e.evaluation)
        )
    );

export const getEvaluationNames = (): Evaluation[] =>
    EvaluationIO.types.map(t => t.value);

export const getIssueNote = (issueId: number) =>
    getIssueNoteList().chain(list =>
        fromNullable(list.find(note => note.issue_id === issueId))
    );

export const getIssueNoteText = (issue: Issue) =>
    getIssueNote(issue.id).map(note => note.text);

export const getGlobalNote = () =>
    getGlobalNoteList().chain(list => fromNullable(list[0]));

export const getGlobalNoteText = (_global: string) =>
    getGlobalNote().map(note => note.text);

export const getOsmObjectForIssue = (issue: Issue) =>
    getOsmResourceId(issue)
        .chain(getOsmObjectRemote)
        .chain(remoteToOption)
        .chain(o => index(0, o.elements));

export const getOsmResourceId = (issue: Issue) =>
    scopeOption()
        .let('id', fromNullable(issue.osm_id))
        .let('type', fromNullable(issue.osm_type))
        .let('version', fromNullable(issue.osm_version))
        .map<OsmResourceId>(({ id, type, version }) => ({ id, type, version }));

export const getSelectedLinkedGroup = () =>
    fromNullable(query('qa/issues/linked/selected'));

export const getLastSelectedId = () => getLastSelectedIssue().map(i => i.id);

export const getIssuesInDays = (
    listOpt: Option<Issue[]>,
    numberOfDays: number
) => {
    const lastWeek = new Date();
    const lastWeekDate = lastWeek.getDate() - numberOfDays;
    lastWeek.setDate(lastWeekDate);
    return listOpt.map(list =>
        list.filter(i => Date.parse(i.timestamp) > lastWeek.valueOf())
    );
};

export const getIssuesInDuration = (
    listOpt: Option<Issue[]>,
    duration: Duration
) => {
    switch (duration) {
        case 'week':
            return getIssuesInDays(listOpt, 7);
        case 'month':
            return getIssuesInDays(listOpt, 30);
        case 'year':
            return getIssuesInDays(listOpt, 365);
        default:
            return getIssuesInDays(listOpt, 500);
    }
};
export const getNbIssue = () => {
    const filter = getGlobalFilter();
    return getIssuesInDuration(
        getGlobalFilteredIssues(filter),
        getRenderGlobalDuration()
    ).getOrElse([]).length;
};

export const getNewIssues = () => {
    const filter = getGlobalFilter();
    return getIssuesInDuration(
        getGlobalFilteredIssues(filter),
        getRenderGlobalDuration()
    )
        .getOrElse([])
        .filter(i => i.status === 'RAISED');
};

export const getNbNewIssues = () => getNewIssues().length;

export const getNbCriticity = (criticality: IssueCriticality) =>
    getNewIssues().filter(i => i.criticity === criticality).length;

export const getUserFromIssue = (issue: Issue) => fromNullable(issue.osm_user);

export const getLinkedRemote = (
    id: number // issueId
) =>
    fromNullable(query('qa/issues/linked/remote')[id.toString()]).getOrElse(
        remoteNone
    );

export const getLinkedRemoteList = () => query('qa/issues/linked/remote');

export const getLinkedIssuesList = () => query('qa/issues/linked');

export const getLinkedIssues = (id: number) =>
    fromNullable(getLinkedIssuesUnchecked(id));

export const getLinkedIssuesUnchecked = (id: number) =>
    query('qa/issues/linked').find(l => l.id === id)!;

// export const getManualLinkedGroup = (issueId: number) => {
//     const groups = remoteToOption(getLinkedRemote(issueId)).map(idList => idList.map(id => getLinkedIssues(id))).getOrElse([]);
//     return fromNullable(groups.find(li => li.map(g => g.link === 'MANUAL').getOrElse(false)))
//         .getOrElse(none);

// };

export const getGlobalFilter = () => query('qa/issues/filter/global');
export const getGlobalFilteredIssues = (filter: GlobalFilter) => {
    switch (filter) {
        case 'newOrStalled':
            return getIssueList().map(list =>
                list.filter(
                    i => i.status === 'RAISED' || i.status === 'STALLED'
                )
            );
        case 'new':
            return getIssueList().map(list =>
                list.filter(i => i.status === 'RAISED')
            );
        case 'stalled':
            return getIssueList().map(list =>
                list.filter(i => i.status === 'STALLED')
            );
        case 'allIssues':
            return getIssueList();
    }
};

export const filterFromCriticality = (
    listOpt: Option<Issue[]>,
    criticality: IssueCriticality
) => listOpt.map(list => list.filter(i => i.criticity === criticality));

export const getGlobalInfoFilteredIssues = () => {
    const filter = getGlobalFilter();
    let list = getGlobalFilteredIssues(filter);
    const durationList = getIssuesInDuration(list, getRenderGlobalDuration());
    getFilterCriticality().chain(
        c =>
            (list = filterFromCriticality(durationList, c).map(l =>
                l.filter(i => i.status === 'RAISED')
            ))
    ); //only in global-info => check only new issues
    return list;
};

export const getFilterCriticality = () =>
    fromNullable(query('qa/issues/filter/criticality'));
export const getRenderBaselayer = () => query('app/baselayer/select');

export const getMapillaryFeatures = (issueId: number) =>
    getMapillaryImage(issueId).map(grp => grp.features);
export const getMapillaryKey = (issueId: number) =>
    getMapillaryFeatures(issueId).chain(f =>
        f.length > 0 ? some(f[0].properties.key) : none
    );

export const fetchIssueGeometryData =
    (gt: GeometryType): FetchData =>
    () => {
        const issueGeometries = query('qa/issue/geometries/collection');
        const selected = getSelectedIssueIds();
        const features: Feature[] = [];
        selected.forEach(id =>
            fromNullable(issueGeometries[id]).map(({ current, previous }) => {
                if (previous !== null && previous !== undefined) {
                    previous.features.forEach(f => {
                        if (f !== null && f.geometry.type === gt) {
                            features.push({
                                ...f,
                                geometry: wgs84toLB72(f.geometry),
                                properties: { version: 'previous' },
                            });
                        }
                    });
                }
                if (current !== null && current !== undefined) {
                    current.features.forEach(f => {
                        if (f !== null && f.geometry.type === gt) {
                            features.push({
                                ...f,
                                geometry: wgs84toLB72(f.geometry),
                                properties: { version: 'current' },
                            });
                        }
                    });
                }
            })
        );

        return right(
            some({
                type: 'FeatureCollection',
                features,
            })
        );
        // const issueGeometries = query('qa/issue/geometries');
        // const selected = getSelectedIssueIds();
        // const features: Feature[] = [];
        // selected.forEach(id => fromNullable(issueGeometries[id])
        //     .map(({ current, previous }) => {
        //         if (previous !== null && previous.geometry.type === gt) {
        //             features.push({
        //                 ...previous,
        //                 geometry: wgs84toLB72(previous.geometry),
        //                 properties: { version: 'previous' },
        //             });
        //         }
        //         if (current !== null && current.geometry.type === gt) {
        //             features.push({
        //                 ...current,
        //                 geometry: wgs84toLB72(current.geometry),
        //                 properties: { version: 'current' },
        //             });
        //         }
        //     }));

        // return right(some({
        //     type: 'FeatureCollection',
        //     features,
        // }));
    };

logger('loaded');
